import React, { FC, useMemo, useState } from 'react';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import { AddPayRules } from 'components/dialog/pay_rules/AddPayRules';
import { EditPayRules } from 'components/dialog/pay_rules/EditPayRules';
import { InfoPayRules } from 'components/dialog/pay_rules/InfoPayRules';
import { DeletePayRule } from 'components/dialog/pay_rules/DeletePayRules';
import { PayRuleDB } from 'repository/timekeeper/@types/PayRuleModel';
import { buildSettingsBasePath, buildSettingsCrudPath, parseSettingsCrudRoute } from 'utils/settingsCrudRouting';

interface ContextMenuProps {
    selected: PayRuleDB[];
    rows: PayRuleDB[];
    contextMenu: any;
    setContextMenu: (value: any) => void;
    reloading: () => void;
    cleanSelected: () => void;
}

export const ContextMenu: FC<ContextMenuProps> = ({ selected, rows, contextMenu, setContextMenu, reloading, cleanSelected }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [openDelete, setOpenDelete] = useState(false);
    const [mode, setMode] = useState<'add' | 'edit' | 'info' | 'delete'>('delete');
    const basePath = buildSettingsBasePath('pay_rules');

    const modalRoute = useMemo(() => parseSettingsCrudRoute(location.pathname, 'pay_rules'), [location.pathname]);
    const openAdd = modalRoute.action === 'add';
    const openEdit = modalRoute.action === 'update' && modalRoute.id !== null;
    const openShow = modalRoute.action === 'detail' && modalRoute.id !== null;

    const selectedRowFromRoute = useMemo(() => {
        if (modalRoute.id === null) return null;
        return rows.find((row) => Number(row.id) === modalRoute.id) || null;
    }, [rows, modalRoute.id]);
    const selectedRowsForRoute = selectedRowFromRoute ? [selectedRowFromRoute] : selected;

    const closeRouteModal = () => {
        navigate(basePath);
        cleanSelected();
    };

    const options = [
        {
            name: t('settings.pay_rules.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSettingsCrudPath('pay_rules', 'add'));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: false,
            selected: false,
            onlyOne: false,
            hidden: (JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'timekeeper.CreatePayRule') ? false : true,
            dialog: <AddPayRules open={openAdd} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal(); }} rows={rows} reloading={reloading} cleanSelected={cleanSelected} mode={'add'} />,
        },
        {
            name: t('settings.pay_rules.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                const current = selected[0];
                if (!current?.id) return;
                navigate(buildSettingsCrudPath('pay_rules', 'update', current.id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selected.length !== 1 ? true : false,
            selected: false,
            onlyOne: true,
            hidden: selected.length !== 0 || !(JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: any) => permision === 'timekeeper.UpdatePayRule') ? true : false,
            dialog: <EditPayRules open={openEdit} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal(); }} rows={rows} rowsSelected={selectedRowsForRoute} reloading={reloading} cleanSelected={cleanSelected} />,
        },
        {
            name: t('settings.pay_rules.table.context_menu.info'),
            icon: <VisibilityIcon />,
            click: () => {
                const current = selected[0];
                if (!current?.id) return;
                navigate(buildSettingsCrudPath('pay_rules', 'detail', current.id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selected.length !== 1 ? true : false,
            selected: true,
            onlyOne: false,
            hidden: selected.length !== 0 || !(JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: any) => permision === 'timekeeper.PayRule') ? true : false,
            dialog: <InfoPayRules open={openShow} setOpen={(nextOpen) => { if (!nextOpen) closeRouteModal(); }} rows={rows} cleanSelected={cleanSelected} rowsSelected={selectedRowsForRoute} mode={'info'} />,
        },
        {
            name: t('settings.pay_rules.table.context_menu.delete'),
            icon: <DeleteIcon color="error" />,
            click: () => {
                setMode('delete');
                setOpenDelete(true);
                setContextMenu(null);
            },
            color: 'error',
            divider: false,
            disabled: true, //selectedRow.length <= 0 ? true : false,
            selected: true,
            onlyOne: false,
            hidden: selected.length !== 0 || !(JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: any) => permision === 'timekeeper.DeletePayRule') ? true : false,
            dialog: <DeletePayRule open={openDelete} setOpen={setOpenDelete} reloading={reloading} selected={selected} cleanSelected={cleanSelected} />,
        },
        {
            name: t('settings.pay_rules.table.context_menu.reload'),
            icon: <ReplayIcon />,
            click: () => {
                reloading();
                setContextMenu(null);
            },
            color: '',
            divider: true,
            disabled: false,
            selected: false,
            onlyOne: false,
            hidden: false,
        },
    ];

    return (
        <>
            <Menu
                open={Boolean(contextMenu)}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ boxShadow: 'none' }}
            >
                {options.map((option, index) => (
                    <div key={option.name} hidden={option.onlyOne && selected?.length !== 1}>
                        {option.divider && <Divider />}
                        <MenuItem onClick={option.click} disabled={option.disabled}>
                            <ListItemIcon>{option.icon}</ListItemIcon>
                            <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                        </MenuItem>
                    </div>
                ))}
            </Menu>
            {options.map((option) => option.dialog && <React.Fragment key={`dialog-${option.name}`}>{option.dialog}</React.Fragment>)}
        </>
    );
};
